import { datadogRum } from '@datadog/browser-rum'
import { DATADOG_INTAKE_ORIGIN } from 'src/analytics/analyticsHosts'

/**
 * EX-40884: forwards CSP violations to RUM so the report-only policy is measured
 * across all staging traffic instead of only where someone has devtools open.
 *
 * `disposition` is the field that matters when reading these: 'report' means the
 * hardened report-only policy would have blocked something (the soak signal),
 * 'enforce' means the live policy actually did block it (a production bug that
 * predates this and should be triaged, not soaked).
 */
export const CSP_VIOLATION_ACTION = 'csp_violation'

/**
 * Volume control. Two layers, because a page that violates once usually violates
 * in a loop -- a blocked script that gets retried, a polling XHR to a disallowed
 * host.
 *
 * 1. Dedupe on (disposition, directive, blockedUri). This module is started from
 *    client.tsx module scope, which runs once per HARD page load, so the set
 *    spans the whole SPA session -- client-side navigation across many screens
 *    still reports a given violation once.
 * 2. This ceiling, as a backstop for a page that cycles through many distinct
 *    blocked URIs.
 *
 * Expected steady state is zero events per session; a host missing from the
 * policy on a hot path is 1-3. The ceiling should never be reached in practice,
 * so it is set low enough to bound the pathological case rather than to
 * accommodate it.
 */
const MAX_REPORTED_VIOLATIONS = 10

/**
 * Violation URIs can carry query strings, and on this app those can contain
 * tokens and ids. Only the origin and path are needed to identify what was
 * blocked, so everything after them is dropped rather than shipped to RUM.
 */
const stripQuery = (uri: string): string => {
  if (!uri) {
    return uri
  }

  try {
    const { origin, pathname } = new URL(uri)
    return `${origin}${pathname}`
  } catch {
    // Not a URL -- CSP uses bare tokens like 'inline', 'eval', and 'data' here.
    return uri.split(/[?#]/)[0]
  }
}

/**
 * Whether a violation is our own RUM upload being blocked.
 *
 * If the intake origin is what got blocked, the report cannot be delivered
 * anyway -- and letting these through would burn the per-session cap before any
 * real miss is seen, which is how a broken policy hides behind a quiet-looking
 * soak.
 *
 * Compares the PARSED origin, never a string prefix: a prefix test also matches
 * a lookalike like https://browser-intake-datadoghq.com.evil.example, and
 * silently dropping that from the soak is the opposite of what this guard is for.
 * A URI that will not parse -- CSP's bare keywords, 'inline' and friends -- is
 * not our transport, so it falls through and gets reported.
 */
const isSelfTransport = (blockedUri: string): boolean => {
  try {
    return new URL(blockedUri).origin === DATADOG_INTAKE_ORIGIN
  } catch {
    return false
  }
}

/**
 * The subset of SecurityPolicyViolationEvent this module reads.
 *
 * Live events satisfy it structurally; so do the plain snapshots the SSR shell
 * buffers into window.__CSP_VIOLATIONS__ before this bundle exists
 * (CSP_VIOLATION_BUFFER_BOOTSTRAP in src/server.ts). Both go through one code
 * path so a buffered violation is redacted, deduped, and capped exactly like a
 * live one.
 */
export type CspViolationEventLike = Pick<
  SecurityPolicyViolationEvent,
  | 'disposition'
  | 'effectiveDirective'
  | 'violatedDirective'
  | 'blockedURI'
  | 'documentURI'
  | 'sourceFile'
  | 'lineNumber'
  | 'statusCode'
>

declare global {
  interface Window {
    __CSP_VIOLATIONS__?: CspViolationEventLike[]
  }
}

export const buildCspViolationContext = (
  event: CspViolationEventLike,
): Record<string, unknown> => ({
  disposition: event.disposition,
  directive: event.effectiveDirective || event.violatedDirective,
  blockedUri: stripQuery(event.blockedURI),
  documentUri: stripQuery(event.documentURI),
  sourceFile: event.sourceFile ? stripQuery(event.sourceFile) : undefined,
  lineNumber: event.lineNumber || undefined,
  statusCode: event.statusCode || undefined,
})

/**
 * Attaches the listener and returns its teardown. Each call creates its own
 * handler and its own dedupe set, so call it once per page load (client.tsx) --
 * calling it twice would double-report.
 */
export const startCspViolationReporting = (): (() => void) => {
  const reported = new Set<string>()

  const handleViolation = (event: CspViolationEventLike): void => {
    if (reported.size >= MAX_REPORTED_VIOLATIONS) {
      return
    }

    if (isSelfTransport(event.blockedURI)) {
      return
    }

    const context = buildCspViolationContext(event)
    const key = `${context.disposition}|${context.directive}|${context.blockedUri}`

    if (reported.has(key)) {
      return
    }

    reported.add(key)
    datadogRum.addAction(CSP_VIOLATION_ACTION, context)
  }

  // Drain what the SSR shell buffered before this bundle existed, then take over.
  // Clearing the global is the shell listener's off switch: it cannot be detached
  // from here (its handler reference lives in an inline script), so instead it
  // early-returns once the buffer is gone, rather than accumulating events
  // forever that nothing will read. There is no gap between the two -- this block
  // is synchronous, so no violation can be dispatched partway through it.
  const buffered = window.__CSP_VIOLATIONS__
  window.__CSP_VIOLATIONS__ = undefined
  buffered?.forEach(handleViolation)

  document.addEventListener('securitypolicyviolation', handleViolation)

  return (): void => {
    document.removeEventListener('securitypolicyviolation', handleViolation)
  }
}
